import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ShiftType } from '@ms/timesheets/dist/models/shift-type';
import { useDispatch, useSelector } from 'react-redux';
import { CalculateHoursLogic } from 'features/pending-timesheets/logic/calculate-hours.logic';
import { IOnlineApprovalTimeEntry } from 'features/pending-timesheets/models/online-approval-time-entry.model';
import { PendingTimesheet } from 'features/pending-timesheets/models/pending-timesheets.model';
import renderMobile from './timesheet-edit-entry.mobile';
import renderDesktop from './timesheet-edit-entry.desktop';
import { RootState } from 'store/root-reducer';
import {
    setChangedTimeEntries,
    setSelectedTimesheetTimeEntries,
} from 'features/pending-timesheets/redux/time-entries-slice';
import { setSelectedTimesheet } from 'features/pending-timesheets/redux/selected-timesheet-slice';
import SmartComponent from 'utils/smart-component';
import { TextLogic } from 'features/pending-timesheets/logic/text.logic';
import { TimesheetLogic } from 'features/pending-timesheets/logic/timesheet.logic';
import { TypedLocalState } from 'utils/redux-hooks';
import { UpdateLogic } from 'features/pending-timesheets/logic/update.logic';
import { WhiteListedShiftTypes } from 'features/pending-timesheets/models/whitelisted-shift-types';
import { AppConfig } from 'features/config/config.model';
import './timesheet-edit-entry.scss';
import { TimeEntryLogic } from '@ms/timesheets/dist/logic/time-entry.logic';
import { TimesheetPackage } from 'features/pending-timesheets/models/timesheet-package.model';

export interface ITimesheetEditEntryProps {
    entryKey: string;
    timeEntry: IOnlineApprovalTimeEntry;
    timesheetPackage?: TimesheetPackage;
}

export interface ITimesheetEditEntryState {
    handleChangeDate: (e: string, shouldUpdate?: boolean) => void;
    inputClass: (property: string) => string;
    timeEntry: IOnlineApprovalTimeEntry;
    updateEntry: (entryName: string, e: string | boolean) => void;
    updatedTimeEntry: IOnlineApprovalTimeEntry;
    selectedTimesheet: PendingTimesheet;
    shiftTypes: string[];
    entryKey: string;
    showAddDelete: boolean;
    deleteMsg: string;
    totalHours: string;
    isTimeEntryChanged: boolean;
    timeInValid: boolean;
    timeOutValid: boolean;
    lunchInValid: boolean;
    lunchOutValid: boolean;
    showCostCenters: boolean;
    costCenters: {
        id: string;
        name: string;
    }[];
}

const useInitialState = (props: ITimesheetEditEntryProps): ITimesheetEditEntryState => {
    // Local state
    const [updatedTimeEntry, setUpdatedTimeEntry]: TypedLocalState<IOnlineApprovalTimeEntry> = useState(
        props.timeEntry,
    );
    const [totalHours, setTotalHours]: TypedLocalState<string> = useState('0.00');
    const [timeInValid, setTimeInValid]: TypedLocalState<boolean> = useState(true);
    const [timeOutValid, setTimeOutValid]: TypedLocalState<boolean> = useState(true);
    const [lunchInValid, setLunchInValid]: TypedLocalState<boolean> = useState(true);
    const [lunchOutValid, setLunchOutValid]: TypedLocalState<boolean> = useState(true);

    // Global state
    const dispatch = useDispatch();
    const originalTimeEntries = useSelector((state: RootState) => state.timeEntries.originalTimeEntries);
    const originalEntry = UpdateLogic.getOriginalTimeEntry(updatedTimeEntry, originalTimeEntries);
    const selectedTimesheet = useSelector(
        (state: RootState) => state.selectedTimesheet.selectedTimesheet as PendingTimesheet,
    );
    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const showAddDelete = TimesheetLogic.showAddDelete(selectedTimesheet.onlineApprovalDate, config);
    const selectedTimesheetTimeEntries = useSelector(
        (state: RootState) => state.timeEntries.selectedTimesheetTimeEntries as IOnlineApprovalTimeEntry[],
    );

    const deleteMsg = TimesheetLogic.createDeleteMsg(updatedTimeEntry);

    // useEffects
    // add new time entries to state
    useEffect(() => {
        if (
            selectedTimesheetTimeEntries.length &&
            updatedTimeEntry?.id !== null &&
            updatedTimeEntry?.id !== undefined
        ) {
            recalc(updatedTimeEntry);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedTimeEntry, selectedTimesheetTimeEntries]);

    // Check for bad time values. Set new total hours.
    useEffect(() => {
        let _totalHours = TimeEntryLogic.calcHours(updatedTimeEntry)?.toFixed(2);

        const { hasAnyMissingValues, missingValues } = TimesheetLogic.hasAnyMissingTimes(updatedTimeEntry);

        setTimeInValid(!missingValues.timeIn);
        setTimeOutValid(!missingValues.timeOut);
        setLunchInValid(!missingValues.lunchIn);
        setLunchOutValid(!missingValues.lunchOut);

        if (hasAnyMissingValues || isNaN(parseInt(_totalHours))) {
            _totalHours = 'Invalid';
        } else {
            if (_totalHours === '0.00') {
                _totalHours = '----';
            }
        }

        setTotalHours(_totalHours);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedTimeEntry?.timeIn, updatedTimeEntry?.timeOut, updatedTimeEntry?.lunchOut, updatedTimeEntry?.lunchIn]);

    // Add updated time entry values to state
    const updateValues = (timeEntry: IOnlineApprovalTimeEntry) => {
        timeEntry.hasLunch = TimesheetLogic.calculateHasLunch(timeEntry);

        const timeEntries = UpdateLogic.updateTimeEntry(timeEntry, selectedTimesheetTimeEntries);
        const original = UpdateLogic.getOriginalTimeEntry(timeEntry, originalTimeEntries);

        if (!timeEntry.isNew && !UpdateLogic.compareTimeEntries(timeEntry, original)) {
            timeEntry.hasChanges = true;
        } else if (!timeEntry.isNew) {
            timeEntry.hasChanges = false;
        }

        setUpdatedTimeEntry(timeEntry);
        dispatch(setChangedTimeEntries(timeEntry));
        dispatch(setSelectedTimesheetTimeEntries(timeEntries));
    };

    const recalc = (timeEntry: IOnlineApprovalTimeEntry) => {
        const updatedTimesheet = CalculateHoursLogic.calculateTimesheetTotals(
            timeEntry,
            selectedTimesheetTimeEntries,
            selectedTimesheet,
        );
        dispatch(setSelectedTimesheet(updatedTimesheet));
    };

    // Filter shift list down and format for select component
    const shiftTypes = Object.keys(ShiftType)
        ?.filter((key: string) => !isNaN(Number(ShiftType[key])))
        ?.filter((shiftType: string) => WhiteListedShiftTypes.includes(ShiftType[shiftType]));

    shiftTypes?.forEach((shiftType: string, index: number) => {
        shiftTypes[index] = TextLogic.spaceOutCapitalization(shiftType);
    });

    // Make Regular appear first
    shiftTypes.splice(shiftTypes.indexOf('Regular'), 1);
    shiftTypes.unshift('Regular');

    // Format and run validation on updated time entry values
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateEntry = (property: string, value: any): void => {
        let newValue = value;

        if (property === 'shiftType') {
            const ValueWithNoSpaces = value?.replace(/ /g, '');
            newValue = ShiftType[ValueWithNoSpaces];
        } else if (property === 'entryDate') {
            newValue = dayjs(value).toDate();
        } else if (
            property === 'timeIn' ||
            property === 'timeOut' ||
            property === 'lunchIn' ||
            property === 'lunchOut'
        ) {
            if (dayjs(value, 'HHmm')?.isValid()) {
                newValue = dayjs(value, 'HHmm')?.format('HHmm');
            } else {
                newValue = null;
            }
        }

        const newValues = {
            ...updatedTimeEntry,
            [property]: newValue,
        } as IOnlineApprovalTimeEntry;

        const { hasAnyMissingValues } = TimesheetLogic.hasAnyMissingTimes(newValues);
        newValues.isInvalid = hasAnyMissingValues;

        updateValues(newValues);
    };

    const inputClass = (property: string): string => {
        if (!originalEntry || !updatedTimeEntry) {
            return 'font-weight-semibold primary';
        }

        return originalEntry[property]?.toString() !== updatedTimeEntry[property]?.toString()
            ? 'font-weight-semibold primary'
            : 'font-weight-normal text-muted';
    };

    const handleChangeDate = (e: string, shouldUpdate = true) => {
        if (shouldUpdate) {
            updateEntry('entryDate', e);
        }
    };

    const isTimeEntryChanged = !UpdateLogic.compareTimeEntries(updatedTimeEntry, originalEntry);

    const costCentersOptions = () => {
        if (
            props.timesheetPackage?.timesheet?.showCostCenters &&
            props.timesheetPackage?.costCenters != null &&
            props.timesheetPackage?.costCenters.length > 0
        ) {
            const costCentersOrdered = props.timesheetPackage.costCenters.sort((a, b) => {
                // Sort by isDefault first (true values first)
                if (a.isDefault !== b.isDefault) {
                    return (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
                }
                // Then sort by costCenterName
                return a.costCenterName.localeCompare(b.costCenterName);
            });

            const mappedCostCenters = costCentersOrdered.map((cc) => ({
                id: cc.costCenterId,
                name: cc.costCenterName,
            }));

            // Add empty option if:
            // 1. The time entry doesn't have a cost center assigned, OR
            // 2. It's a new time entry and there's no default cost center
            const hasCostCenterAssigned = props.timeEntry.costCenterId != null;
            const hasDefaultCostCenter = props.timesheetPackage.costCenters.some((cc) => cc.isDefault);
            const shouldShowEmptyOption =
                (!hasCostCenterAssigned && !updatedTimeEntry.isNew) ||
                (updatedTimeEntry.isNew && !hasDefaultCostCenter);

            if (shouldShowEmptyOption) {
                mappedCostCenters.unshift({ id: '', name: ' ' });
            }

            return mappedCostCenters;
        } else {
            return [];
        }
    };

    return {
        handleChangeDate,
        inputClass,
        selectedTimesheet,
        shiftTypes,
        timeEntry: props.timeEntry,
        updateEntry,
        updatedTimeEntry,
        entryKey: props.entryKey,
        showAddDelete,
        deleteMsg,
        totalHours,
        isTimeEntryChanged,
        timeInValid,
        timeOutValid,
        lunchInValid,
        lunchOutValid,
        showCostCenters: props.timesheetPackage?.timesheet?.showCostCenters ?? false,
        costCenters: costCentersOptions(),
    };
};

export { renderDesktop as TimesheetEditEntryDesktop, renderMobile as TimesheetEditEntryMobile };

export default SmartComponent<ITimesheetEditEntryProps, ITimesheetEditEntryState>(
    useInitialState,
    renderDesktop,
    renderMobile,
);
